<template>
  <div class="editable-input-wrapper">
    <div class="header-wrapper">
      <p v-if="title" class="field-title">{{ title }}</p>
      <p v-if="description" class="field-description hoverable-blue" @click="$emit('description-clicked')">
        {{ description }}
      </p>
    </div>
    <div class="input-wrapper" :class="{ focused: focused, incorrect: !validInput }" @mouseover="hover = true" @mouseleave="hover = false">
      <input
        :ref="reference"
        :key="inputKey"
        :type="currentInputType"
        :placeholder="placeholder"
        :disabled="disabled"
        :name="nameToSave"
        :autocomplete="editableAutocomplete ? editableAutocomplete : 'new-password'"
        :autocapitalize="autoCapitalize"
        :class="{ disabled: disabled, password: inputType === 'password' }"
        :value="input"
        @keyup.enter="$emit('focus-next-input', $event)"
        @input="checkInput"
        @focus="focusIn()"
        @blur="
          focusOut()
          $emit('focus-out', $event.target.value)
        "
      />
      <img v-if="showIcon" :src="iconSrc" @click="iconClicked" />
    </div>
    <p v-if="!validInput && incorrectValueMsg" class="footer incorrect">
      {{ incorrectValueMsg }}
    </p>
    <p v-else-if="footer" class="footer field-footer">
      {{ footer }}
    </p>
  </div>
</template>

<script>
export default {
  components: {},
  props: {
    method: { type: Function },
    value: [String, Number],
    title: String,
    reference: {
      type: [String, Boolean],
      default: false,
    },
    description: String,
    dbname: String,
    placeholder: String,
    editableAutocomplete: String,
    autoCapitalize: {
      type: String,
      default: 'off',
    },
    disabled: {
      // should input be disabled
      type: Boolean,
      default: false,
    },
    inputType: {
      // i.e. password, number
      type: String,
      default: 'text',
    },
    footer: String, // additional info
    incorrectValueMsg: String, // message displayed when user makes a mistake
    validationFunction: {
      // user can provide custom validation function i.e. email regex
      type: Function,
      default: function (v) {
        return v !== null && v !== ''
      },
    },
    restoreValueOnIncorrect: {
      // restore last correct value of the input field on focus out
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    outsideValidation: Number,
    lazyValidation: {
      type: Boolean,
      default: false,
    },
    vatRestriction: {
      type: Boolean,
      default: false,
    },
    vatIdString: {
      type: String,
      default: '',
    },
    disableAutoComplete: {
      type: Boolean,
      default: false,
    },
    nameToSave: {
      type: String,
      default: '',
    },
  },
  /* USAGE EXAMPLE
    <editable-input id="input-name-wrapper"
    reference="name"
    title="Name"
    v-model="userName"
    @focus-out="changeName"
    incorrectValueMsg="Name cannot be empty."
    restoreValueOnIncorrect/>
    */
  data() {
    return {
      input: this.value,
      initialInput: this.value, // for restoring purposes
      focused: false,
      validInput: true,
      currentInputType: this.inputType, // for password preview icon
      hovered: false,
      inputKey: 0,
    }
  },
  computed: {
    iconSrc() {
      if (this.input !== '') {
        return require('@/assets/_desktop/_shared/' +
          (this.inputType === 'password' // if input type is password
            ? this.currentInputType === 'password' // if password is hidden
              ? 'password_preview' // icon to show password
              : 'password_hide' // else icon to hide password
            : 'clear') + // else icon to clear input
          '.svg')
      } else {
        return ''
      }
    },
    showIcon() {
      if (this.disabled || this.inputType === 'number') return false

      if (this.inputType === 'password') {
        // password icon if sth is typed
        return this.input !== ''
      } else {
        // clear icon in sth is typed AND field is focused on
        return this.input !== '' && (this.focused || this.hover)
      }
    },
  },
  mounted() {
    if (this.autofocus) {
      this.$refs[this.reference].focus()
    }
    if (this.disableAutoComplete) {
      this.disableAutoCompleteFunction()
    }
  },
  methods: {
    disableAutoCompleteFunction() {
      let elements = document.querySelectorAll('[autocomplete="off"]')

      if (!elements) {
        return
      }
    },
    checkInput(e) {
      this.$emit('input', e.target.value)

      if (this.vatRestriction) {
        let value = e.target.value
        if (this.vatIdString === 'PL') {
          value = value.replace(/[^0-9]/g, '')
          value = value.substring(0, 10)
        } else {
          value = value.replace(/[^0-9a-zA-Z]/g, '')
          if (value.length > 2) {
            let valTemp = value.substring(0, 2)
            value = value.replace(/[^0-9a-zA-Z]/g, '')
            if (!/[a-zA-Z]{2}/.test(valTemp)) {
              value = value.replace(/^/, this.vatIdString)
            } else {
              // value = value.substr(2,16)
              // value = value.replace (/^/,valTemp);
            }
          }
          value = value.substring(0, 14)
        }
        this.input = value.toUpperCase()
        this.$forceUpdate()
      } else {
        this.input = e.target.value
        this.validInput = this.lazyValidation ? true : this.validationFunction(this.input)
        // this.validInput = this.lazyValidation ? this.validInput : this.validationFunction(this.input);
      }
    },
    focusIn() {
      if (this.reference) {
        this.$refs[this.reference].focus()
      }
      this.initialInput = this.input // update initial value
      this.focused = true
    },
    focusOut() {
      if (this.validInput) {
        // if correct input
        this.initialInput = this.input // update initial value
      } else {
        // incorrect input
        if (this.restoreValueOnIncorrect) {
          this.input = this.initialInput // restore initial value
          this.validInput = true // restored value is valid
        }
      }
      this.focused = false
    },
    iconClicked() {
      if (this.inputType === 'password') {
        // if password input
        if (this.currentInputType === 'password') {
          // show/hide characters
          this.currentInputType = 'text'
        } else {
          this.currentInputType = 'password'
        }
      } else {
        // else clear input
        this.input = ''
        this.validInput = false
        this.$emit('input', this.input)
      }
    },
    validate() {
      this.validInput = this.validationFunction(this.input)
      return this.validInput
    },
    setIncorrect() {
      this.validInput = false
    },
    setCorrect() {
      this.validInput = true
    },
    setInput(input) {
      this.input = input
    },
  },
}
</script>

<style lang="scss" scoped>
.editable-input-wrapper {
  position: relative; // positioning footers
  .header-wrapper {
    font-size: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;

    .field-title {
      @include label;
      margin-right: 3px;
      margin-bottom: 6px;
    }

    .field-description {
      @include font-rule('averta-bold');
      font-size: 12px;
      line-height: 18px;
      color: $bluebell;
      cursor: pointer;
      margin: 0 0 6px 0;
      white-space: nowrap;
    }
  }

  .input-wrapper {
    @include font-rule('averta-regular');
    .disabled {
      color: $bluebell;
      background-color: $white-lilac;
    }
    @include animate-border-default(2px, 0);
    &.focused {
      &:not(.incorrect) {
        @include animate-border-focused();
      }
      input[type='text'] {
        padding-right: 40px;
      }
    }

    position: relative;
    display: flex;
    flex-direction: row;

    input {
      font-size: 14px;
      padding: 10px;
      width: 100%;
      color: $black;
      border: 1px solid $flax-bloom;
      background-color: #ffffff;
      transition: border-color 0.2s;

      &:focus {
        border-color: $blue;
        //box-shadow: 0 0 0 3px rgba($blue, 0.1);
      }

      & ~ img {
        position: absolute; // shared
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        opacity: 0.8;

        cursor: pointer;

        &:hover {
          opacity: 1;
        }
      }

      &.password ~ img {
        // password icon
        width: 24px;
        height: 24px;
      }

      /*&:hover ~ img, img:hover {
                display: unset;
            }*/

      &:disabled {
        opacity: 1;
      }
    }

    /*&.focused img {
            display: unset;
        }*/

    &.incorrect {
      input {
        border: 1px solid $red;
      }
    }
  }

  .footer {
    @include font-rule('averta-regular');
    font-size: 13px;
    color: $bluebell;
    margin-top: 6px;

    //position: absolute;

    &.incorrect {
      color: $red;
      bottom: -28px;
    }
  }
}
</style>
